import { LoadingOutlined } from '@ant-design/icons';
import { notification } from 'antd';
import moment from 'moment-timezone';
import { createElement } from 'react';
import { DATE_FORMAT, STATUS_ALL, STATUS_PAID, STATUS_UNPAID } from 'consts';
import { getInvoiceReportDownload } from 'services/reports';
import { NotificationType, ResponseData } from 'types';
import { getInvoiceDownload } from 'services/invoices';
import { EPackageType } from 'enums';
import { CheckoutResponse, MainUserPackagePlan } from 'services/payment';

export const truncateText = (text: string, maxLength: number = 15) => {
  if (!text) return '';
  return text?.length > maxLength ? text?.slice(0, maxLength) + '...' : text;
};

export const calculateSafePercent = (
  remainBalance: number,
  invoiceAmount: number
): number => {
  if (invoiceAmount <= 0) return 0;

  const percent = (remainBalance / invoiceAmount) * 100;

  return Math.min(Math.max(percent, 0), 100);
};

export const getStatusLabel = (confirmedStatus, translation) => {
  if (confirmedStatus === STATUS_ALL) {
    return translation.reports.All;
  }

  if (Array.isArray(confirmedStatus)) {
    if (confirmedStatus.includes(STATUS_PAID)) {
      return translation.reports.Paid;
    }
    if (confirmedStatus.includes(STATUS_UNPAID)) {
      return translation.reports.Unpaid;
    }
  }

  return translation.reports.All;
};

export function isEmpty(val) {
  if (val === undefined) return true;

  if (
    typeof val == 'function' ||
    typeof val == 'number' ||
    typeof val == 'boolean' ||
    Object.prototype.toString.call(val) === '[object Date]'
  )
    return false;

  if (val == null || val.length === 0)
    // null or 0 length array
    return true;

  if (typeof val == 'object') {
    // empty object

    var r = true;

    for (const f in val) r = false;

    return r;
  }

  return false;
}

export const waitForReportCompletion = async (
  reportId: string,
  translation
) => {
  const maxAttempts = 10;
  const interval = 4000;

  for (let i = 0; i < maxAttempts; i++) {
    try {
      const response = await getInvoiceReportDownload(reportId);

      if (response?.data) {
        return response;
      }
    } catch (e) {}

    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  throw new Error(translation.reports.AllWait);
};

export const waitForInvoicePdfCompletion = async (
  targetInvoiceId: any,
  translation
) => {
  const maxAttempts = 10;
  const interval = 4000;
  let lastError: unknown;

  for (let i = 0; i < maxAttempts; i++) {
    try {
      const response = await getInvoiceDownload(targetInvoiceId);
      if (response?.data) {
        return response;
      }
    } catch (e) {}

    await new Promise((resolve) => setTimeout(resolve, interval));
  }

  throw new Error(translation.reports.AllWait);
};

export const openNotification = (
  message: string,
  type: NotificationType = 'warning',
  duration: number = 2,
  key: string = '',
  hasLoadingIcon: boolean = false
) => {
  notification[type]({
    message,
    duration,
    key,
    icon: hasLoadingIcon
      ? createElement(LoadingOutlined, { spin: true })
      : undefined,
  });
};

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const filterPaidPackagePlans = <T extends MainUserPackagePlan>(
  plans: T[]
) =>
  plans.filter((plan) => plan.packageType?.toLowerCase() !== EPackageType.FREE);

export type SubscriptionUrgencyLevel = 0 | 1 | 2 | 3;

const getUrgencyLevelFromDays = (
  daysRemaining: number
): SubscriptionUrgencyLevel => {
  if (daysRemaining <= 7) return 3;
  if (daysRemaining <= 14) return 2;
  if (daysRemaining <= 30) return 1;
  return 0;
};

export const getSubscriptionUrgencyLevel = (
  expirationDate?: string | null
): SubscriptionUrgencyLevel => {
  if (!expirationDate) return 0;

  const parsed = moment(
    expirationDate,
    [DATE_FORMAT, 'YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601],
    true
  );

  const expiry = parsed.isValid() ? parsed : moment(expirationDate);

  if (!expiry.isValid()) return 0;

  const daysRemaining = expiry
    .startOf('day')
    .diff(moment().startOf('day'), 'days');

  return getUrgencyLevelFromDays(daysRemaining);
};

export const openCheckoutPaymentInNewTab = (response: {
  data: CheckoutResponse;
}): boolean => {
  const url = response.data?.url?.trim();

  if (!url) {
    return false;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
};
